import React from "react";
import { makeStyles } from "@material-ui/core";
import { Container, Grid, Typography } from "@material-ui/core";
import "./Privacy.css";

// Images
import backgroundImage from "../../../Images/Hero/BackgroundImage.jpg";

const useStyles = makeStyles((theme) => ({
  privacy: {
    minHeight: "100vh",
    width: "100%",
    overflow: "hidden",
    display: "flex",
    background:
      "linear-gradient(61.54deg, #e5e7eb -48.56%, #e5e7eb -48.55%, #f8fbfa 72.22%)",
    position: "relative",
    alignItems: "flex-start",
    paddingTop: "100px",
    paddingBottom: "100px",
  },
  heroImage: {
    backgroundImage: `url(${backgroundImage})`,
    opacity: "0.3",
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: "0",
  },
  container: {
    zIndex: "1",
    padding: "0 24px",
    [theme.breakpoints.down("sm")]: {
      margin: "30px auto 20px",
    },
    [theme.breakpoints.down("xs")]: {
      margin: "20px auto 40px",
    },
  },
  title: {
    fontWeight: "700 !important",
    fontFamily: "Roboto !important",
    fontSize: "46px !important",
    textTransform: "capitalize !important",
    marginBottom: "10px !important",
    color: "#2D2D2D !important",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "36px !important",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "28px !important",
    },
  },
  titleHighlight: {
    background: "var(--color-accent-orange)",
    height: "15px",
    width: "100%",
    maxWidth: "400px",
    margin: "0 auto 40px",
    [theme.breakpoints.down("xs")]: {
      marginBottom: "30px",
    },
  },
  sectionTitle: {
    fontWeight: "700 !important",
    fontFamily: "Roboto !important",
    fontSize: "24px !important",
    color: "#07516C !important",
    marginTop: "40px !important",
    marginBottom: "20px !important",
    [theme.breakpoints.down("xs")]: {
      fontSize: "20px !important",
      marginTop: "30px !important",
    },
  },
  paragraph: {
    fontWeight: "400 !important",
    fontSize: "16px !important",
    lineHeight: "1.6 !important",
    marginBottom: "20px !important",
    fontFamily: "Roboto !important",
    color: "#2D2D2D !important",
    [theme.breakpoints.down("xs")]: {
      fontSize: "15px !important",
    },
  },
  listItem: {
    fontWeight: "400 !important",
    fontSize: "16px !important",
    lineHeight: "1.6 !important",
    marginBottom: "10px !important",
    fontFamily: "Roboto !important",
    color: "#2D2D2D !important",
    paddingLeft: "20px !important",
    position: "relative",
    "&:before": {
      content: '"•"',
      position: "absolute",
      left: "0",
      color: "#07516C",
      fontWeight: "bold",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "15px !important",
    },
  },
  lastUpdated: {
    fontWeight: "400 !important",
    fontSize: "14px !important",
    fontStyle: "italic !important",
    color: "var(--color-neutral-500) !important",
    marginTop: "40px !important",
    textAlign: "center",
    fontFamily: "Roboto !important",
  },
  contentContainer: {
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    padding: "40px",
    [theme.breakpoints.down("sm")]: {
      padding: "30px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "20px",
    },
  },
}));

const Privacy = () => {
  const classes = useStyles();
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className={classes.privacy}>
      <div className={classes.heroImage}></div>
      <Container maxWidth="lg" className={classes.container}>
        <Grid container>
          <Grid item xs={12}>
            <div className={classes.contentContainer}>
              <Typography variant="h1" className={classes.title}>
                Privacy Policy
              </Typography>
              <div className={classes.titleHighlight}></div>

              <Typography variant="body1" className={classes.paragraph}>
                Kinetx Co LLC ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you visit our website 
                or use our services.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                1. Information We Collect
              </Typography>

              <Typography variant="h3" className={classes.sectionTitle} style={{ fontSize: "18px", marginTop: "20px" }}>
                Information You Provide to Us
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We collect information you provide directly to us, such as when you:
              </Typography>
              <div className={classes.listItem}>Contact us through our website contact form</div>
              <div className={classes.listItem}>Request consultations or quotes for our services</div>
              <div className={classes.listItem}>Subscribe to our communications</div>
              <div className={classes.listItem}>Engage with our customer support</div>
              <div className={classes.listItem}>Enter into service agreements with us</div>

              <Typography variant="body1" className={classes.paragraph}>
                This information may include your name, email address, phone number, company information, 
                and details about your IT infrastructure needs.
              </Typography>

              <Typography variant="h3" className={classes.sectionTitle} style={{ fontSize: "18px", marginTop: "20px" }}>
                Information We Collect Automatically
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                When you access our website, we may automatically collect certain information, including:
              </Typography>
              <div className={classes.listItem}>Log information (IP address, browser type, operating system)</div>
              <div className={classes.listItem}>Usage information (pages visited, time spent on pages)</div>
              <div className={classes.listItem}>Device information (device type, unique device identifiers)</div>
              <div className={classes.listItem}>Location information (general geographic location)</div>

              <Typography variant="h2" className={classes.sectionTitle}>
                2. How We Use Your Information
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We use the information we collect to:
              </Typography>
              <div className={classes.listItem}>Provide, maintain, and improve our IT infrastructure services</div>
              <div className={classes.listItem}>Respond to your inquiries and provide customer support</div>
              <div className={classes.listItem}>Process service requests and manage our business relationship</div>
              <div className={classes.listItem}>Send you technical notices, updates, and administrative messages</div>
              <div className={classes.listItem}>Communicate with you about our services, promotions, and industry insights</div>
              <div className={classes.listItem}>Analyze website usage to improve user experience</div>
              <div className={classes.listItem}>Comply with legal obligations and protect our rights</div>

              <Typography variant="h2" className={classes.sectionTitle}>
                3. Information Sharing and Disclosure
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We do not sell, trade, or otherwise transfer your personal information to third parties except in the 
                following circumstances:
              </Typography>
              <div className={classes.listItem}>With your explicit consent</div>
              <div className={classes.listItem}>To trusted service providers who assist in operating our website or conducting our business</div>
              <div className={classes.listItem}>To comply with legal requirements, court orders, or government requests</div>
              <div className={classes.listItem}>To protect our rights, property, or safety, or that of our users or others</div>
              <div className={classes.listItem}>In connection with a business transfer, merger, or acquisition</div>

              <Typography variant="h2" className={classes.sectionTitle}>
                4. Data Security
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. However, no method 
                of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee 
                absolute security.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                5. Cookies and Tracking Technologies
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Our website uses cookies and similar tracking technologies to enhance your browsing experience. 
                Cookies are small data files stored on your device that help us:
              </Typography>
              <div className={classes.listItem}>Remember your preferences and settings</div>
              <div className={classes.listItem}>Analyze website traffic and user behavior</div>
              <div className={classes.listItem}>Provide personalized content and advertisements</div>
              <div className={classes.listItem}>Enable certain website functionality</div>

              <Typography variant="body1" className={classes.paragraph}>
                You can control cookie preferences through your browser settings, though disabling cookies may 
                affect website functionality.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                6. Third-Party Services
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Our website may integrate with third-party services including:
              </Typography>
              <div className={classes.listItem}>Google Analytics for website analytics</div>
              <div className={classes.listItem}>Google Tag Manager for marketing tracking</div>
              <div className={classes.listItem}>Zoho SalesIQ for customer chat support</div>
              <div className={classes.listItem}>Microsoft 365 for email communications</div>

              <Typography variant="body1" className={classes.paragraph}>
                These services have their own privacy policies governing the collection and use of your information.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                7. Data Retention
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer 
                need your information, we will securely delete or anonymize it.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                8. Your Rights and Choices
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Depending on your location, you may have certain rights regarding your personal information, including:
              </Typography>
              <div className={classes.listItem}>The right to access and receive a copy of your personal information</div>
              <div className={classes.listItem}>The right to rectify inaccurate or incomplete information</div>
              <div className={classes.listItem}>The right to request deletion of your personal information</div>
              <div className={classes.listItem}>The right to restrict or object to processing</div>
              <div className={classes.listItem}>The right to data portability</div>
              <div className={classes.listItem}>The right to withdraw consent where applicable</div>

              <Typography variant="body1" className={classes.paragraph}>
                To exercise these rights, please contact us using the information provided below.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                9. Children's Privacy
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Our services are not intended for individuals under the age of 18. We do not knowingly collect 
                personal information from children under 18. If we become aware that we have collected such 
                information, we will take steps to delete it promptly.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                10. International Data Transfers
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                Your information may be transferred to and processed in countries other than your country of residence. 
                These countries may have data protection laws that differ from those of your country. We take 
                appropriate measures to ensure your information receives an adequate level of protection.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                11. Changes to This Privacy Policy
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                We may update this Privacy Policy from time to time to reflect changes in our practices or for 
                other operational, legal, or regulatory reasons. We will notify you of any material changes by 
                posting the updated policy on our website and updating the "Last Updated" date.
              </Typography>

              <Typography variant="h2" className={classes.sectionTitle}>
                12. Contact Us
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
              </Typography>
              <Typography variant="body1" className={classes.paragraph}>
                <strong>Kinetx Co LLC</strong><br />
                Email: techs@kinetx.co<br />
                Phone: 917-719-4889<br />
                Website: kinetx.co
              </Typography>

              <Typography variant="body1" className={classes.paragraph}>
                For privacy-specific inquiries, you may also contact our Data Protection Officer at the email address above 
                with "Privacy Inquiry" in the subject line.
              </Typography>

              <Typography variant="body2" className={classes.lastUpdated}>
                Last updated: {currentDate}
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default Privacy;
